
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { ReferralStorageService } from '@/services/referral/ReferralStorageService';
import { SecureAuthTokenService } from '@/services/auth/SecureAuthTokenService';
import { indexedDBService } from '@/services/offline';
import { classifyAuthError, formatAuthError } from '@/services/auth/errorHandling';
import { getEmailRedirectUrl } from '@/utils/authRedirectUtils';
import type { User, Session } from '@supabase/supabase-js';
// Define AuthResponse type since it's not exported in current Supabase version
interface AuthResponse {
  data: {
    user: User | null;
    session: Session | null;
  } | null;
  error: Error | null;
}

// Types not exported in current Supabase version
type AuthError = Error;
type UserMetadata = Record<string, unknown>;

export const useAuthOperations = (
  toastShownRef: React.MutableRefObject<{ signedIn: boolean; adminAccess: boolean }>,
  signOutInProgressRef: React.MutableRefObject<boolean>
) => {
  const secureAuthService = SecureAuthTokenService.getInstance();

  const signIn = useCallback(async (email: string, password: string): Promise<AuthResponse> => {
    try {

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.session) {
        // Store session securely
        await secureAuthService.storeSession(data.session);


        if (!toastShownRef.current.signedIn) {

          toast({
            title: "Signed in successfully",
            description: "Welcome back!",
          });
          toastShownRef.current.signedIn = true;
        }
      }

      return { data, error: null };
    } catch (error: unknown) {
      const authError = error as AuthError | Error;
      console.error('🔐 Error signing in:', authError);

      // Classify error and provide specific guidance
      const classification = classifyAuthError(error);
      const formattedError = formatAuthError(classification);

      toast({
        title: formattedError.title,
        description: formattedError.description,
        variant: "destructive",
      });

      return { data: null, error: authError as AuthError };
    }
  }, [toastShownRef, secureAuthService]);

  const signUp = useCallback(async (email: string, password: string, userData?: UserMetadata): Promise<AuthResponse> => {
    try {

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: getEmailRedirectUrl(),
        },
      });

      if (error) throw error;

      if (data.user) {
        // Store session securely if available
        if (data.session) {
          await secureAuthService.storeSession(data.session);

        }


        toast({
          title: "Account created successfully",
          description: "Please check your email to confirm your account",
        });
      }

      return { data, error: null };
    } catch (error: unknown) {
      const authError = error as AuthError | Error;
      console.error('🔐 Error signing up:', authError);

      // Classify error and provide specific guidance
      const classification = classifyAuthError(error);
      const formattedError = formatAuthError(classification);

      toast({
        title: formattedError.title,
        description: formattedError.description,
        variant: "destructive",
      });

      return { data: null, error: authError as AuthError };
    }
  }, [secureAuthService]);

  const updateUserMetadata = useCallback(async (metadata: UserMetadata) => {
    try {

      const { error } = await supabase.auth.updateUser({
        data: metadata
      });

      if (error) throw error;

      // Don't return the data, just complete successfully
    } catch (error: unknown) {
      const authError = error as AuthError | Error;
      console.error('🔐 Error updating user metadata:', authError);
      throw authError;
    }
  }, []);

  const signOut = useCallback(async () => {
    if (signOutInProgressRef.current) {

      return;
    }
    
    try {

      signOutInProgressRef.current = true;
      
      // Clear all storage first to prevent phantom states
      localStorage.clear();
      sessionStorage.clear();
      ReferralStorageService.clearReferralData();

      // Clear secure auth storage
      await secureAuthService.clearStoredAuth();

      // Clear IndexedDB to prevent profile data leakage between users
      try {
        await indexedDBService.clearAllData();
        console.log('🗑️ IndexedDB cleared during logout');
      } catch (indexedDBError) {
        console.error('⚠️ Failed to clear IndexedDB during logout:', indexedDBError);
        // Continue with logout even if IndexedDB clearing fails
      }

      
      // Reset state
      toastShownRef.current.signedIn = false;
      toastShownRef.current.adminAccess = false;
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut({ scope: 'global' });

      if (error) {
        console.error('🔐 Supabase sign out error:', error);
        // Don't throw here, we still want to redirect even if Supabase fails
      } else {
        // Sign out successful
      }
      
      toast({
        title: "Signed out successfully",
      });
      
      // Force redirect to login

      window.location.href = '/auth/login';
    } catch (error: unknown) {
      const authError = error as AuthError | Error;
      console.error('🔐 Error during sign out:', authError);

      // Classify error and provide specific guidance
      const classification = classifyAuthError(error);
      const formattedError = formatAuthError(classification);

      toast({
        title: formattedError.title,
        description: formattedError.description,
        variant: "destructive",
      });
      // Force redirect even on error
      window.location.href = '/auth/login';
    } finally {
      signOutInProgressRef.current = false;
    }
  }, [toastShownRef, signOutInProgressRef, secureAuthService]);

  return {
    signIn,
    signUp,
    updateUserMetadata,
    signOut
  };
};
